import {
  Assignment,
  CheckCircle,
  Error,
  Info,
  Notifications,
  Pets,
  Warning,
} from "@mui/icons-material";
import VaccinesIcon from "@mui/icons-material/Vaccines";
import {
  alpha,
  Avatar,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { BsFillBox2HeartFill } from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import { PRIMARY_BLUE } from "../../../../enums/Colors";
import { isFemaleGender } from "../../../pets/utils/Functions";
import useStringTranslation from "../../../../hooks/useStringTranslation";
import PetAvatar from "../../../pets/singleView/display/PetAvatar";
import { petViewUrl } from "../../../../enums/UrlPaths";

const SingleBoxCard = ({ box }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const hasErrorAlert = box?.alerts?.some((alert) =>
    ["error", "urgent"].includes(String(alert.alertSeverity).toLowerCase()),
  );

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.background.paper,
          0.8,
        )} 0%, ${alpha(theme.palette.background.default, 0.4)} 100%)`,
        backdropFilter: "blur(10px)",
        border: `2px solid ${
          hasErrorAlert
            ? theme.palette.error.main
            : alpha(theme.palette.primary.main, 0.1)
        }`,
        boxShadow: hasErrorAlert
          ? `0 0 15px ${alpha(theme.palette.error.main, 0.6)}`
          : "none",
        transition: "all 0.3s ease-in-out",
        "&:hover": {
          transform: "scale(1.01)",
          boxShadow: hasErrorAlert
            ? `0 0 35px ${alpha(theme.palette.error.main, 0.8)}`
            : `0 0px 60px ${alpha(theme.palette.primary.main, 0.2)}`,
          borderColor: hasErrorAlert
            ? theme.palette.error.main
            : alpha(theme.palette.primary.main, 0.3),
        },
      }}
    >
      <CardActionArea
        onClick={() => navigate(`/saps/boxes/box/${box.id}`)}
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
        }}
      >
        <CardContent
          sx={{
            p: 3,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            "&:last-child": { pb: 3 },
          }}
        >
          <Header box={box} />
          <PetsList box={box} />
          <Tasks box={box} />
          <Alerts box={box} />
        </CardContent>
      </CardActionArea>
    </Card>
  );
};
function Section({ icon, title, children, sx }) {
  return (
    <Box sx={{ mb: 3, ...sx }}>
      <Typography
        variant="h6"
        fontWeight={600}
        gutterBottom
        sx={{ display: "flex", alignItems: "center", gap: 1 }}
      >
        {icon} {title}
      </Typography>
      {children}
    </Box>
  );
}

function Header({ box }) {
  const { t } = useTranslation();
  const theme = useTheme();
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", mb: 3 }}>
      <Avatar
        sx={{
          mr: 2,
          width: 50,
          height: 50,
          boxShadow: `0 0px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
          border: `2px solid ${theme.palette.background.paper}`,
          bgcolor: PRIMARY_BLUE,
          color: "white",
        }}
      >
        <BsFillBox2HeartFill />
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, lineHeight: 1.2, mb: 0.5 }}
          noWrap
        >
          {box?.name}
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ fontWeight: 600 }}
        >
          {box?.pets?.length > 0 && (
            <Typography>
              {box?.pets?.length === 1
                ? t("petsCount", { count: 1 })
                : t("petsCount_plural", { count: box?.pets?.length })}
            </Typography>
          )}
        </Typography>
      </Box>
      <PetAvatar isGroup petList={box?.pets} />
    </Box>
  );
}

function PetsList({ box }) {
  const { t } = useTranslation();

  return (
    <Section
      icon={<Pets fontSize="small" />}
      title={t("PetsFilterLabel")}
      sx={{ flex: 1 }}
    >
      {box?.pets?.length > 0 ? (
        <Stack spacing={1}>
          {box?.pets?.slice(0, 3).map((pet) => (
            <PetInBox pet={pet} />
          ))}
          {box?.pets?.length > 3 && (
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
              textAlign="center"
            >
              +{box?.pets?.length - 3} {t("morePets")}
            </Typography>
          )}
        </Stack>
      ) : (
        <NoData title={t("noPetsInBox")} />
      )}
    </Section>
  );
}

function PetInBox({ pet = {} }) {
  const species = useStringTranslation(pet?.type, "title");
  const theme = useTheme();
  const navigate = useNavigate();

  const getGenderColor = (gender) =>
    isFemaleGender(gender) ? "hotpink" : PRIMARY_BLUE;

  return (
    <Box
      key={pet?.id}
      onClick={(e) => {
        e.stopPropagation();
        navigate(petViewUrl(pet?.id, pet?.userId));
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        py: 1,
        px: 1.5,
        borderRadius: 2,
        backgroundColor: alpha(theme.palette.primary.main, 0.03),
        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        cursor: "pointer",
        position: "relative",
        zIndex: 1,
        fontWeight: 600,
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        "&:hover": {
          transform: "scale(1.05)",
          boxShadow: `0 4px 12px rgba(0,0,0,0.15)`,
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
        },
      }}
    >
      <Typography variant="body2" fontWeight={600} noWrap sx={{ flex: 1 }}>
        {pet?.name}
      </Typography>
      {pet?.vaccinated && (
        <Avatar
          sx={{
            ml: 1,
            width: 22,
            height: 22,
            bgcolor: theme.palette.primary.main,
          }}
        >
          <VaccinesIcon sx={{ fontSize: 14, color: "white" }} />
        </Avatar>
      )}
      <Chip
        label={species}
        size="small"
        sx={{
          color: getGenderColor(pet?.gender),
          ml: 1,
          fontSize: "0.7rem",
          height: 24,
          fontWeight: 700,
          borderRadius: 1.5,
        }}
      />
    </Box>
  );
}

function Tasks({ box }) {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Section icon={<Assignment fontSize="small" />} title={t("Therapies")}>
      {box?.todos?.length > 0 ? (
        <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
          {box?.todos?.slice(0, 2).map((todo) => (
            <Chip
              key={todo?.id}
              label={todo?.title}
              size="small"
              icon={<CheckCircle fontSize="small" />}
              color="primary"
              variant="filled"
              sx={{
                fontSize: "0.75rem",
                fontWeight: 600,
                borderRadius: 2,
                border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                "& .MuiChip-label": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "120px",
                },
              }}
            />
          ))}
          {box?.todos?.length > 2 && (
            <Chip
              label={`+${box?.todos?.length - 2}`}
              size="small"
              variant="filled"
              color="primary"
              sx={{
                fontSize: "0.75rem",
                fontWeight: 700,
                borderRadius: 2,
              }}
            />
          )}
        </Stack>
      ) : (
        <NoData title={t("noTasksForBox")} />
      )}
    </Section>
  );
}

function Alerts({ box }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const getAlertIcon = (status) => {
    switch (String(status).toLowerCase()) {
      case "error":
        return <Error color="error" fontSize="small" />;
      case "warning":
        return <Warning color="warning" fontSize="small" />;
      case "info":
        return <Info color="info" fontSize="small" />;
      default:
        return <Info fontSize="small" />;
    }
  };

  return (
    <Section
      icon={<Notifications fontSize="small" />}
      title={t("alertsTitle")}
      sx={{ mt: "auto" }}
    >
      {box?.alerts?.length > 0 ? (
        <Stack spacing={1.5}>
          {box?.alerts?.slice(0, 2).map((alert) => (
            <Box
              key={alert?.id}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
                p: 2,
                borderRadius: 2,
                backgroundColor: alpha(
                  theme.palette[String(alert?.alertSeverity).toLowerCase()]
                    .main,
                  0.08,
                ),
                border: `2px solid ${alpha(
                  theme.palette[String(alert?.alertSeverity).toLowerCase()]
                    .main,
                  0.2,
                )}`,
              }}
            >
              {getAlertIcon(alert?.alertSeverity)}
              <Typography
                sx={{
                  fontWeight: 600,
                  flex: 1,
                  lineHeight: 1.4,
                  fontSize: "0.95rem",
                  wordBreak: "break-all",
                }}
              >
                {alert?.description}
              </Typography>
            </Box>
          ))}
          {box?.alerts?.length > 2 && (
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
              textAlign="center"
            >
              +{box?.alerts?.length - 2} {t("moreAlerts")}
            </Typography>
          )}
        </Stack>
      ) : (
        <NoData title={t("noAlertsFound")} />
      )}
    </Section>
  );
}

export default SingleBoxCard;

function NoData({ title }) {
  return (
    <Typography color={"GrayText"} variant="subtitle2">
      {title}
    </Typography>
  );
}
